/**
 * Room requests, built ON TOP of the deployed tasks lane.
 *
 * Bobbie (2026-08-01): "The room request should show up for Kim as a task as
 * well as as a page where she can manage that." Staff ask for a room, Ms. Kim
 * approves (which actually books, through the normal transactional composer)
 * or declines with a reason.
 *
 * WHY A TASK AND NOT A NEW COLLECTION: the Firestore rules deploy pipeline is
 * broken, so no new collections, fields, or query shapes can ship. The tasks
 * lane is the one deployed place where any active staff member can create a
 * structured work item assigned to somebody else, with per-assignee
 * completion, an assignee-scoped provable query, and notifications on create.
 * So a room request IS a task: kind 'general' (the deployed kind enum is
 * closed - no new kinds), assigned to Kim's active Director account, dueAt set to the
 * requested date so an unactioned request goes overdue on its own.
 *
 * The request's facts (room, window, purpose, requester) ride in the task
 * DESCRIPTION: a human-readable summary first, then a small machine block
 * this module can parse back out. A task whose description does not parse is
 * simply not a room request - it renders as an ordinary task everywhere and
 * the management page ignores it. No parse failure can lose data, because
 * the human summary carries the same facts in plain words.
 *
 * This module is PURE on purpose: no Firebase, no React, no imports. That is
 * what lets tests/room-requests.test.mjs drive it under
 * node --experimental-strip-types without loading the app.
 */

/** Mirrors ReservationKind in lib/reservations.ts without importing Firebase. */
export type RoomRequestKind = 'class' | 'rehearsal' | 'event' | 'block';

const REQUEST_KINDS: readonly RoomRequestKind[] = ['class', 'rehearsal', 'event', 'block'];

/**
 * The marker line the parser keys on. Versioned so a future format change can
 * keep reading old requests instead of orphaning them.
 */
export const ROOM_REQUEST_MARKER = '[room-request v1]';

/** The single studio account Bobbie designated to approve room requests. */
export const ROOM_APPROVER_EMAIL = 'kim@thestudioldg.com';
/** Canonical Auth/Firestore uid for Kim. Email alone is client-editable. */
export const ROOM_APPROVER_UID = 'tRIQY0oB85QWbjlW0JS12YQpvXi2';

const ROOM_REQUEST_BLOCK_START = `\n\n${ROOM_REQUEST_MARKER}\n`;

export interface RoomApproverCandidate {
  uid: string;
  email?: string | null;
  role?: string | null;
  status?: string | null;
}

/** True only for the one account allowed to work the room-request desk. */
export function isRoomApproverUser(
  candidate: RoomApproverCandidate | null | undefined
): boolean {
  return !!candidate
    && candidate.role === 'director'
    && candidate.status === 'active'
    && candidate.uid.trim() === ROOM_APPROVER_UID
    && (candidate.email ?? '').trim().toLowerCase() === ROOM_APPROVER_EMAIL
    && candidate.uid.trim().length > 0;
}

/**
 * Resolve Kim's one canonical active Director account. The uid is the
 * authorization identity; email remains a human-auditable cross-check. A
 * second Director copying Kim's editable profile email cannot become her.
 */
export function selectRoomApproverUid(
  candidates: readonly RoomApproverCandidate[]
): string | null {
  const matchingUids = new Set(
    candidates
      .filter(isRoomApproverUser)
      .map((candidate) => candidate.uid.trim())
  );
  return matchingUids.size === 1 ? [...matchingUids][0] : null;
}

export interface RoomRequestDraft {
  roomId: string;
  roomName: string;
  /** Studio wall time, datetime-local format: YYYY-MM-DDTHH:MM. */
  startLocal: string;
  endLocal: string;
  kind: RoomRequestKind;
  offeringId: string | null;
  purpose: string;
  note?: string;
  requesterUid: string;
  requesterName: string;
}

export interface RoomRequestDetails {
  taskId: string;
  roomId: string;
  roomName: string;
  startLocal: string;
  endLocal: string;
  kind: RoomRequestKind;
  offeringId: string | null;
  purpose: string;
  note: string | null;
  requesterUid: string;
  requesterName: string;
  /** Present only on server-created parent/dancer requests. Legacy and staff requests omit it. */
  requesterAudience?: 'family';
}

/**
 * One-line sanitizer for every value that lands in the machine block. The
 * block is line-oriented with ' | ' separators, so newlines and pipes in a
 * purpose like "hip hop | extra run" would corrupt the fields next to it.
 */
function sanitizeInline(value: string, max: number): string {
  return value
    .replace(/[\r\n|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

/**
 * Parse a datetime-local string (YYYY-MM-DDTHH:MM) as studio wall time.
 * Exactly the format /calendar/new reads from its ?start=&end= params, so a
 * request round-trips into the composer with zero timezone math.
 */
export function parseLocalDateTime(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** 5:00 PM style clock label. Fixed ASCII output, no locale drift in tests. */
export function formatClockTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m < 10 ? `0${m}` : m} ${suffix}`;
}

function shortDateLabel(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Tue, Aug 5 from 5:00 PM to 6:00 PM" (second date shown only if it differs). */
export function roomRequestWhenLabel(details: Pick<RoomRequestDetails, 'startLocal' | 'endLocal'>): string {
  const start = parseLocalDateTime(details.startLocal);
  const end = parseLocalDateTime(details.endLocal);
  if (!start || !end) return '';
  const sameDay = details.startLocal.slice(0, 10) === details.endLocal.slice(0, 10);
  if (sameDay) {
    return `${shortDateLabel(start)} from ${formatClockTime(start)} to ${formatClockTime(end)}`;
  }
  return `${shortDateLabel(start)} ${formatClockTime(start)} to ${shortDateLabel(end)} ${formatClockTime(end)}`;
}

/** YYYY-MM-DD of the requested day, for /calendar?d= links. */
export function roomRequestDateKey(details: Pick<RoomRequestDetails, 'startLocal'>): string {
  return details.startLocal.slice(0, 10);
}

/**
 * Build the task title + description + dueAt for a room request.
 *
 * Everything here has to pass the DEPLOYED hasTaskCreateShape() untouched:
 * title is a plain non-empty string, description is a plain string, and the
 * caller creates the task through the existing useCreateTask() hook so the
 * doc shape stays byte-identical to every other task.
 *
 * Throws on an invalid window because a request with no parseable time can
 * never be approved into a booking.
 */
export function buildRoomRequestTask(draft: RoomRequestDraft): {
  title: string;
  description: string;
  dueAt: Date;
} {
  const start = parseLocalDateTime(draft.startLocal);
  const end = parseLocalDateTime(draft.endLocal);
  if (!start || !end) throw new Error('Pick a valid start and end time.');
  if (end.getTime() <= start.getTime()) throw new Error('End must be after start.');

  const roomId = sanitizeInline(draft.roomId, 80);
  const roomName = sanitizeInline(draft.roomName, 80) || 'a studio room';
  const purpose = sanitizeInline(draft.purpose, 120) || 'Room time';
  const note = sanitizeInline(draft.note ?? '', 500);
  const requesterUid = sanitizeInline(draft.requesterUid, 128);
  const requesterName = sanitizeInline(draft.requesterName, 80) || 'A staff member';
  const kind: RoomRequestKind = REQUEST_KINDS.includes(draft.kind) ? draft.kind : 'event';
  const offeringId = draft.offeringId ? sanitizeInline(draft.offeringId, 80) : '';
  if (!roomId) throw new Error('Pick a room.');
  if (!requesterUid) throw new Error('Sign in required.');

  const when = roomRequestWhenLabel({ startLocal: draft.startLocal, endLocal: draft.endLocal });
  const firstName = requesterName.split(' ')[0] || requesterName;
  const title = `Room request: ${roomName} ${when} (${firstName})`.slice(0, 160);

  const humanLines = [
    `${roomName} on ${when}.`,
    `Requested by ${requesterName}. For: ${purpose}.`,
  ];
  if (note) humanLines.push(`Note: ${note}`);
  humanLines.push('Review it under Calendar > Room requests. Booking it there is the only step that actually reserves the room.');

  const machineLines = [
    ROOM_REQUEST_MARKER,
    `room: ${roomId} | ${roomName}`,
    `when: ${draft.startLocal} | ${draft.endLocal}`,
    `kind: ${kind}`,
    `offering: ${offeringId || '-'}`,
    `for: ${purpose}`,
  ];
  if (note) machineLines.push(`note: ${note}`);
  machineLines.push(`by: ${requesterUid} | ${requesterName}`);

  return {
    title,
    description: `${humanLines.join('\n')}\n\n${machineLines.join('\n')}`,
    dueAt: start,
  };
}

/** Cheap pre-check so list surfaces can skip the full parse on plain tasks. */
export function isRoomRequestTask(description: string | null | undefined): boolean {
  return typeof description === 'string' && description.includes(ROOM_REQUEST_BLOCK_START);
}

/**
 * Ordinary tasks belong to every listed assignee. A room request's ACTION
 * buttons belong only to Kim, even when an older document still contains
 * Bobbie's legacy staff slot - that is what viewerIsStaff protects against,
 * since a stray staff uid (a director who is not Kim, a manager, and so on)
 * must never see approve/book controls the Firestore rule will reject.
 *
 * A room request's READ - the card itself, note included - is different:
 * Bobbie, 2026-08-14, on a dancer-submitted request, "Parents just need to
 * be able to see that it's happened." A guardian riding along in
 * assigneeUids for exactly that reason (familyRoomRequest.mjs) is not a
 * legacy staff slot, so viewerIsStaff = false lets the request through for
 * them too. Every actionable control (approve/book/decline, and the
 * Director-fallback "Manage request" link) stays gated on isDirector /
 * !isFamilyViewer at the render site regardless - this function only
 * decides whether the row is READABLE, never whether its buttons show.
 *
 * @param viewerIsStaff Defaults to true (the historical, conservative
 * reading) so every existing staff-side caller keeps its exact behavior
 * unless it explicitly says otherwise.
 */
export function taskAssignedToViewer(
  task: {
    assigneeUids: readonly string[];
    description: string | null | undefined;
  },
  /**
   * ONE HUMAN, however she signs in. A Jr Staff holds a dancer sign-in and a
   * staff account (CLAUDE.md, Bobbie 2026-09-07), and a to-do is assigned to
   * ONE of them: the nag keeps one assignee uid per person on purpose,
   * because a task is done only when every assignee has ticked it and two
   * uids would need two ticks from one person. So the widening belongs here,
   * on the read. Callers hand the alias set from the shared helper
   * (lib/jrStaffAliases.ts jrStaffAliasUidsFor); a bare string is still
   * accepted and still answers exactly as it did.
   */
  viewerUid: string | readonly string[],
  viewerIsStaff = true
): boolean {
  const uids = typeof viewerUid === 'string' ? [viewerUid] : viewerUid;
  return uids.some((uid) => task.assigneeUids.includes(uid))
    && (!isRoomRequestTask(task.description)
      || uids.includes(ROOM_APPROVER_UID)
      || !viewerIsStaff);
}

/**
 * Defense in depth for any surface that renders a task description as plain
 * text. Every place that KNOWS it might be showing a room request should
 * render components/RoomRequestFacts.tsx from the parsed details instead of
 * this string - that gives the labeled WHEN/WHERE/WHAT/WHY/WHO card, not a
 * wall of text. This function exists for everywhere else: a bare description
 * print that forgot to check isRoomRequestTask, or a future surface that
 * never learns about room requests at all. It trims the machine block
 * (ROOM_REQUEST_MARKER onward - raw doc ids, ISO timestamps, a raw uid) off
 * unconditionally, so the worst a forgetful caller can show is the human
 * summary a request already leads with. A description with no marker comes
 * back untouched.
 */
export function stripRoomRequestPayload(description: string | null | undefined): string {
  if (typeof description !== 'string') return '';
  const at = description.indexOf(ROOM_REQUEST_MARKER);
  if (at === -1) return description;
  return description.slice(0, at).trim();
}

/**
 * Parse a room request back out of a task. Returns null for anything that is
 * not a well-formed request: plain tasks, hand-edited descriptions, future
 * format versions. Null means "treat it as an ordinary to-do" - never throw,
 * never guess at a booking window that is not fully there.
 */
export function parseRoomRequestTask(task: {
  id: string;
  description: string | null | undefined;
}): RoomRequestDetails | null {
  const description = task.description;
  if (typeof description !== 'string') return null;
  const markerAt = description.indexOf(ROOM_REQUEST_BLOCK_START);
  if (markerAt === -1) return null;
  const machineBlock = description.slice(markerAt + ROOM_REQUEST_BLOCK_START.length);
  const lines = machineBlock.split('\n').map((l) => l.trim());

  const fields = new Map<string, string>();
  for (const line of lines) {
    if (!line) continue;
    const m = /^([a-z]+):\s?(.*)$/.exec(line);
    if (!m) continue;
    // First occurrence wins so a pasted copy of another request below the
    // real block cannot overwrite the fields above it.
    if (!fields.has(m[1])) fields.set(m[1], m[2]);
  }

  const splitPair = (raw: string | undefined): [string, string] => {
    const value = raw ?? '';
    const at = value.indexOf(' | ');
    if (at === -1) return [value.trim(), ''];
    return [value.slice(0, at).trim(), value.slice(at + 3).trim()];
  };

  const [roomId, roomNameRaw] = splitPair(fields.get('room'));
  const [startLocal, endLocal] = splitPair(fields.get('when'));
  const [requesterUid, requesterNameRaw] = splitPair(fields.get('by'));

  const start = parseLocalDateTime(startLocal);
  const end = parseLocalDateTime(endLocal);
  if (!roomId || !requesterUid || !start || !end) return null;
  if (end.getTime() <= start.getTime()) return null;

  const kindRaw = (fields.get('kind') ?? '').trim();
  const kind: RoomRequestKind = (REQUEST_KINDS as readonly string[]).includes(kindRaw)
    ? (kindRaw as RoomRequestKind)
    : 'event';
  const offeringRaw = (fields.get('offering') ?? '').trim();
  const note = (fields.get('note') ?? '').trim();
  const requesterAudience = fields.get('audience') === 'family' ? 'family' as const : null;

  return {
    taskId: task.id,
    roomId,
    roomName: roomNameRaw || roomId,
    startLocal,
    endLocal,
    kind,
    offeringId: offeringRaw && offeringRaw !== '-' ? offeringRaw : null,
    purpose: (fields.get('for') ?? '').trim() || 'Room time',
    note: note || null,
    requesterUid,
    requesterName: requesterNameRaw || 'A staff member',
    ...(requesterAudience ? { requesterAudience } : {}),
  };
}

/**
 * Deep link into the EXISTING prefilled composer at /calendar/new. Approving
 * never books from the management page: Ms. Kim lands in the composer with
 * everything filled in, and the same slot-lock transaction that guards every
 * other booking stays the gate.
 *
 * The same link is sent to a declined requester. A non-Director following it
 * is redirected by /calendar/new to Find a slot with these params carried
 * along, which re-arms the request card with the same window - so "try
 * again" lands somewhere useful rather than on a form they cannot see.
 *
 * PRODUCER NOTE: since Bobbie's 2026-08-02 fusion ("Kims room request
 * approval should come from all other accounts finding a slot"), the ONLY
 * producer of new requests is /calendar/search - a non-Director picks an
 * open window there and the send builds a request through this module. The
 * old standalone request form on /calendar/new is gone.
 */
export function roomRequestComposerLink(
  details: Pick<
    RoomRequestDetails,
    'taskId' | 'roomId' | 'startLocal' | 'endLocal' | 'kind' | 'offeringId' | 'purpose'
  >,
  /**
   * Kim's edits, when she has made any. The desk books in place now, so this
   * override is for the paths that still need the full form (a repeating
   * booking) plus the "open the full composer" escape hatch - both of which
   * must carry her changes with them, or the link would quietly re-arm the
   * ORIGINAL window and undo the edit she just made.
   */
  plan?: RoomRequestPlan
): string {
  const params = new URLSearchParams({
    roomId: plan?.roomId ?? details.roomId,
    start: plan?.startLocal ?? details.startLocal,
    end: plan?.endLocal ?? details.endLocal,
    kind: plan?.kind ?? details.kind,
    title: plan?.title ?? details.purpose,
  });
  const offeringId = plan ? plan.offeringId : details.offeringId;
  if (offeringId) params.set('offeringId', offeringId);
  // The request task rides along so the composer can close the loop on
  // booking success: complete the task for every Director and tell the
  // requester what was actually booked (completeRoomRequestAfterBooking in
  // roomRequestActions.ts). Harmless on every other path - the non-Director
  // redirect to Find a slot ignores it, and a booking with no reqTask param
  // simply skips the follow-up.
  if (details.taskId) params.set('reqTask', details.taskId);
  return `/calendar/new?${params.toString()}`;
}

/** Route a resolution back to a calendar the original requester can open. */
export function roomRequestCalendarLink(
  details: Pick<RoomRequestDetails, 'requesterAudience'>,
  dateKey: string,
  focusId?: string | null
): string {
  const root = details.requesterAudience === 'family' ? '/family/calendar' : '/calendar';
  return `${root}?d=${dateKey}${focusId ? `&focusId=${focusId}` : ''}`;
}

/** Route a decline back to the requester's own prefilled request surface. */
export function roomRequestRetryLink(details: RoomRequestDetails): string {
  const composer = roomRequestComposerLink(details);
  return details.requesterAudience === 'family'
    ? composer.replace(/^\/calendar\/new/, '/calendar/search')
    : composer;
}

/**
 * What Ms. Kim is about to book, which is not always what was asked for.
 *
 * Bobbie (2026-08-06): "There should be an edit button. But approve and book
 * should [do that] without opening a new page!" So the request desk lets her
 * change the room, the window, what it is for, and the class it links to
 * before she approves. This is the shape those edits travel in, and it is
 * deliberately the same set of fields the composer's own deep link carries -
 * one description of a booking, not two.
 */
export interface RoomRequestPlan {
  roomId: string;
  roomName: string;
  startLocal: string;
  endLocal: string;
  kind: RoomRequestKind;
  offeringId: string | null;
  /** The calendar title. Starts as the request's purpose. */
  title: string;
}

/** The plan that matches a request exactly: nothing changed yet. */
export function planFromRoomRequest(
  details: RoomRequestDetails,
  roomName?: string
): RoomRequestPlan {
  return {
    roomId: details.roomId,
    roomName: roomName || details.roomName,
    startLocal: details.startLocal,
    endLocal: details.endLocal,
    kind: details.kind,
    offeringId: details.offeringId,
    title: details.purpose,
  };
}

export interface BookedRoomRequestFacts {
  roomId: string;
  roomName?: string;
  startAt?: unknown;
  endAt?: unknown;
}

function bookedFactDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (!value || typeof value !== 'object') return null;
  const candidate = value as {
    toDate?: () => Date;
    toMillis?: () => number;
    seconds?: unknown;
  };
  if (typeof candidate.toDate === 'function') {
    const date = candidate.toDate();
    return Number.isFinite(date.getTime()) ? date : null;
  }
  const millis = typeof candidate.toMillis === 'function'
    ? candidate.toMillis()
    : typeof candidate.seconds === 'number'
    ? candidate.seconds * 1000
    : NaN;
  const date = new Date(millis);
  return Number.isFinite(date.getTime()) ? date : null;
}

function bookedFactLocalValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * After a booking notice fails, the recovery card survives a reload. Render
 * the committed booking facts stored by the booking transaction, not the
 * original request that Kim may have edited before approval.
 */
export function roomRequestDetailsFromBookedFacts(
  details: RoomRequestDetails,
  booked: BookedRoomRequestFacts
): RoomRequestDetails {
  const start = bookedFactDate(booked.startAt);
  const end = bookedFactDate(booked.endAt);
  return {
    ...details,
    roomId: booked.roomId || details.roomId,
    roomName: booked.roomName?.trim() || details.roomName,
    startLocal: start ? bookedFactLocalValue(start) : details.startLocal,
    endLocal: end ? bookedFactLocalValue(end) : details.endLocal,
  };
}

/**
 * Round a datetime-local string DOWN to the quarter hour bookings may land on
 * (Bobbie, DECISIONS 102: "Need quarter hours"). It rounded to the half hour
 * until B33, which turned an approved 45 minute request into 30 minutes the
 * moment Kim left the End box.
 */
export function snapLocalToQuarterHour(value: string): string {
  const d = parseLocalDateTime(value);
  if (!d) return value;
  const minutes = d.getMinutes();
  if (minutes % 15 === 0 && d.getSeconds() === 0) return value;
  d.setMinutes(minutes - (minutes % 15), 0, 0);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Plain-words list of everything Kim changed between the request and the
 * booking she is about to make. Empty means she approved it as asked.
 *
 * This is the honesty half of the edit button: a teacher who asked for the
 * Blue Room at 2:30 and gets the Pink Room at 3:00 has to be TOLD, or the
 * approval reads as a yes to something that never happened. The same list
 * shows on Kim's own confirm step, so she can see what she is about to change
 * before she commits it rather than after.
 *
 * Class names are resolved by the caller (this module stays free of any data
 * access); pass a lookup that returns null for an id it does not know, and
 * the line degrades to "a different class" rather than printing a raw id.
 */
export function describeRoomRequestChanges(
  details: RoomRequestDetails,
  plan: RoomRequestPlan,
  offeringName: (id: string | null) => string | null = () => null
): string[] {
  const changes: string[] = [];
  if (plan.roomId !== details.roomId) {
    changes.push(`Room: ${details.roomName} to ${plan.roomName}`);
  }
  const whenBefore = roomRequestWhenLabel(details);
  const whenAfter = roomRequestWhenLabel(plan);
  if (whenBefore !== whenAfter) {
    changes.push(`Time: ${whenBefore} to ${whenAfter}`);
  }
  const titleBefore = details.purpose.trim();
  const titleAfter = plan.title.trim();
  if (titleBefore !== titleAfter) {
    changes.push(`What it is for: "${titleBefore}" to "${titleAfter}"`);
  }
  if ((plan.offeringId ?? null) !== (details.offeringId ?? null)) {
    const before = offeringName(details.offeringId) ?? (details.offeringId ? 'a class' : 'no class');
    const after = offeringName(plan.offeringId) ?? (plan.offeringId ? 'a different class' : 'no class');
    changes.push(`Class: ${before} to ${after}`);
  }
  return changes;
}

/** "Asked just now" / "Asked 3 hours ago" / "Asked 2 days ago". */
export function roomRequestAgeLabel(createdMs: number, nowMs: number): string {
  const elapsed = Math.max(0, nowMs - createdMs);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 2) return 'Asked just now';
  if (minutes < 60) return `Asked ${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Asked ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `Asked ${days} day${days === 1 ? '' : 's'} ago`;
}
